import HBIcon from "@/components/HBIcon";
import HBSwiper from "@/components/HBSwiper";
import { getRelatedBoards, getRelatedBoardsWeb } from "@/services";
import useReq from "@/utils/useReq";
import { useDebounceFn, useSetState } from "ahooks";
import { last } from "lodash";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import BoardCardNew from "../BoardCardNew";
import styles from "../index.module.less";

interface BoardSwiperProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * 推荐画板数据列表
   */
  boardsData: IBoardInfo[];
  /**
   * 标题文本
   */
  title?: string;
  /**
   * 自定义样式类名
   */
  className?: string;
  /**
   * 自定义样式
   */
  style?: React.CSSProperties;
  /**
   * 弹窗堆栈索引，用于生成唯一key
   */
  stackIndex?: number;
  /**
   * 当前pin的ID，用于添加viewSource参数
   */
  pinId?: string | number;
  /**
   * 当前pin
   */
  currentPin: IPin;
  /**
   * 点击"更多"的回调
   */
  onShowMore?: () => void;
  /**
   * 加载更多时的回调，用于追踪当前页数和累积的画板数据
   */
  onLoadMore?: (currentPage: number, allBoards: IBoardInfo[]) => void;
  /**
   * 是否使用新接口（全屏详情页使用新接口，旧版使用旧接口）
   */
  useNewApi?: boolean;
  /**
   * 获取下一个应该拉取的页码（用于与瀑布流共享页码）
   */
  getNextPage?: () => number;
  /**
   * 搜索词（用于埋点）
   */
  searchWord?: string;
  /**
   * 【相似画板】初始数据列表（类似 boardsData）
   */
  similarBoardsData?: IBoardInfo[];
  /**
   * 【相似画板】Hook（用于加载更多和共享状态）
   */
  similarBoardsHook?: {
    hasMore: boolean;
    loading: boolean;
    loadMore: (limit?: number, targetPage?: number) => Promise<IBoardInfo[]>;
  };
}

function BoardSwiper({
  boardsData,
  title = "同在画板",
  className,
  style,
  stackIndex = 0,
  pinId,
  currentPin,
  onShowMore,
  onLoadMore,
  useNewApi = false,
  getNextPage,
  searchWord,
  similarBoardsData = [],
  similarBoardsHook,
  ...restProps // 接收所有额外的 props（包括 data-* 属性）
}: BoardSwiperProps) {
  const [recommendedBoards, setRecommendedBoards] = useSetState<{
    boards: IBoardInfo[];
    page: number;
    hasMore: boolean;
    loading: boolean;
  }>(() => {
    // 初始化：如果有所在画板，直接包含进去
    const initialBoards = currentPin?.board
      ? [currentPin.board, ...boardsData]
      : boardsData;

    // 判断初始是否有更多：如果使用新接口且初始数据，说明没有更多了
    const initialHasMore = useNewApi ? boardsData.length >= 6 : true;

    return {
      boards: initialBoards,
      page: 1,
      hasMore: initialHasMore,
      loading: false,
    };
  });

  // 相似画板状态管理（独立维护自己的数据）
  const [similarBoards, setSimilarBoards] = useSetState<{
    boards: IBoardInfo[];
    hasMore: boolean;
    loading: boolean;
  }>(() => {
    return {
      boards: similarBoardsData || [], // 从 prop 初始化
      hasMore: similarBoardsHook?.hasMore ?? true,
      loading: false,
    };
  });

  const swiperRef = useRef<any>(null);

  // 加载更多推荐画板
  const { run: loadMoreRecommendedBoards } = useReq(
    async () => {
      if (!recommendedBoards.hasMore || recommendedBoards.loading) {
        return;
      }

      setRecommendedBoards({ loading: true });

      let nextPage;
      if (useNewApi && getNextPage) {
        // 使用全局页码（与瀑布流共享）
        nextPage = getNextPage();
      } else {
        // 使用内部页码
        nextPage = recommendedBoards.page + 1;
      }

      let result;
      if (useNewApi) {
        // 全屏详情页使用新接口（基于 page 分页）
        if (nextPage > 100) {
          setRecommendedBoards({ hasMore: false, loading: false });
          return;
        }

        result = await getRelatedBoardsWeb({
          pin_id: pinId,
          limit: 20,
          page: nextPage,
        });
      } else {
        // 旧版详情页使用旧接口（基于 max 游标分页）
        const max =
          last(recommendedBoards.boards)?.seq ||
          last(recommendedBoards.boards)?.board_id;
        result = await getRelatedBoards({
          pin_id: pinId,
          limit: 20,
          ...(max ? { max } : null),
        });
      }

      const newBoards = result?.boards || [];

      // 判断是否还有更多数据
      const hasMore = useNewApi
        ? newBoards.length > 0 // 新接口：通过返回数组是否为空判断
        : result.more; // 旧接口：通过 more 字段判断

      // 更新推荐画板状态（使用 useSetState 的简洁写法）
      const updatedBoards = [...recommendedBoards.boards, ...newBoards];
      if (!hasMore && useNewApi) {
        loadMoreSimilarBoards();
      }

      // 调用父组件的回调，通知当前页数和累积的数据
      if (onLoadMore) {
        onLoadMore(nextPage, updatedBoards);
      }

      setRecommendedBoards({
        boards: updatedBoards,
        page: nextPage,
        hasMore,
        loading: false,
      });
    },
    {
      manual: true,
      onError: () => {
        setRecommendedBoards({ loading: false });
      },
    }
  );

  // 当外部数据源变化时，重置推荐画板状态
  useEffect(() => {
    const initialBoards = currentPin?.board
      ? [currentPin.board, ...boardsData]
      : boardsData;

    const initialHasMore = useNewApi ? boardsData.length >= 6 : true;

    setRecommendedBoards({
      boards: initialBoards,
      page: 1,
      hasMore: initialHasMore,
      loading: false,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardsData, currentPin?.board, useNewApi]);

  // 加载更多相似画板
  const loadMoreSimilarBoards = useCallback(async () => {
    if (!similarBoardsHook || similarBoards.loading || !similarBoards.hasMore) {
      return;
    }

    setSimilarBoards({ loading: true });

    try {
      // 调用 hook 的 loadMore（共享分页状态，同时更新全局数据）
      const newBoards = await similarBoardsHook.loadMore();

      if (newBoards && newBoards.length > 0) {
        // 追加到本地 state（自己的数据）
        setSimilarBoards({
          boards: [...similarBoards.boards, ...newBoards],
          hasMore: similarBoardsHook.hasMore, // 使用共享状态
          loading: false,
        });
      } else {
        setSimilarBoards({
          hasMore: false,
          loading: false,
        });
      }
    } catch (error) {
      setSimilarBoards({ loading: false });
    }
  }, [
    similarBoardsHook,
    similarBoards.boards,
    similarBoards.loading,
    similarBoards.hasMore,
    setSimilarBoards,
  ]);

  // 处理滚动事件，判断是否接近右边界
  // 使用防抖处理，避免动画过程中高频触发导致重复请求
  const handleScrollInternal = useCallback(
    (left: number) => {
      // left 是负数，表示内容向左移动的距离（即向右滚动）
      // 获取滚动容器的信息
      const swiperDom = swiperRef.current?.getScrollDom?.();
      if (!swiperDom) return;

      const viewWidth = swiperDom.offsetWidth; // 可视区域宽度
      // 从可视区域中找到 content DOM（使用子元素查找）
      const contentDom = swiperDom.children[0] as HTMLElement;
      if (!contentDom) return;

      const contentWidth = contentDom.offsetWidth; // 内容总宽度
      const scrolledDistance = Math.abs(left); // 已滚动距离（取绝对值）
      const maxScrollDistance = contentWidth - viewWidth; // 最大可滚动距离

      // 当滚动到距离右边界小于200px时，触发加载更多（只在向右滚动时）
      const threshold = 200;
      const distanceToEnd = maxScrollDistance - scrolledDistance;

      // 只有当 left 是负数（向右滚动）且接近右边界时才触发
      if (left < 0 && distanceToEnd <= threshold) {
        // 使用共享状态判断（避免重复请求）
        if (recommendedBoards.loading || similarBoardsHook?.loading) return;

        // 优先加载推荐画板
        if (recommendedBoards.hasMore) {
          loadMoreRecommendedBoards();
        } else if (similarBoardsHook?.hasMore) {
          loadMoreSimilarBoards();
        }
      }
    },
    [
      recommendedBoards.hasMore,
      recommendedBoards.loading,
      similarBoardsHook?.hasMore,
      similarBoardsHook?.loading,
      loadMoreRecommendedBoards,
      loadMoreSimilarBoards,
    ]
  );

  // 使用防抖处理，延迟 150ms，避免动画过程中高频触发
  const { run: handleScroll } = useDebounceFn(handleScrollInternal, {
    wait: 150,
  });
  // 【合并数据源】计算总数据
  const allBoards = useMemo(() => {
    return [...recommendedBoards.boards, ...similarBoards.boards];
  }, [recommendedBoards.boards, similarBoards.boards]);

  // 【合并数据源】判断是否还有更多（推荐画板或相似画板任一有更多即可）
  const hasMore = useMemo(() => {
    return recommendedBoards.hasMore || similarBoardsHook?.hasMore;
  }, [recommendedBoards.hasMore, similarBoardsHook?.hasMore]);

  // 【合并数据源】判断是否正在加载
  const loading = useMemo(() => {
    return recommendedBoards.loading || similarBoardsHook?.loading;
  }, [recommendedBoards.loading, similarBoardsHook?.loading]);

  // 【合并数据源】计算总画板数（包含「所在画板」）
  const totalBoardCount = useMemo(() => {
    return currentPin?.board
      ? boardsData.length + 1 + similarBoards.boards.length
      : boardsData.length + similarBoards.boards.length;
  }, [boardsData.length, currentPin?.board, similarBoards.boards.length]);

  // 判断是否显示"更多"入口：当前已有数据超过10个
  const showMoreButton = useMemo(() => {
    return allBoards.length >= 6 && onShowMore;
  }, [allBoards.length, onShowMore]);

  // 判断是否显示底部加载提示：只有当初始数据 > 3 时才显示
  const showLoadingFooter = totalBoardCount > 3;

  // 判断是否应用 small 样式：使用稳定的 totalBoardCount 而不是动态的 dataSource.length
  // 避免在数据加载过程中因为数量变化导致布局闪烁
  // 只有当外部数据源显示总数 <= 3，且当前已加载的数据量达到总数时，才应用 small 样式
  // 这样可以避免在数据加载过程中因为数量变化导致布局闪烁
  const shouldApplySmall =
    totalBoardCount <= 3 && allBoards.length >= totalBoardCount;

  return (
    <div
      className={`${styles.boardSwiperContainer} ${className || ""}`}
      style={style}
      {...restProps} // 传递所有额外的 props 到根 div（包括 data-* 属性）
    >
      <div className={styles.boardSwiperHeader}>
        <div className={styles.boardSwiperTitle}>{title}</div>
        {showMoreButton && (
          <div
            className={styles.boardSwiperMore}
            onClick={onShowMore}
            data-gd-click="button_click"
            data-button-name="更多"
            data-button-source="采集详情页_普通采集_为你推荐"
            data-module-name="推荐画板"
          >
            更多
            <HBIcon
              className={styles.boardSwiperMoreIcon}
              type="ic_chevron_right"
            />
          </div>
        )}
      </div>
      <HBSwiper
        ref={swiperRef}
        className={styles.boardSwiper}
        contentClassName={`${styles.boardSwiperContent} ${
          shouldApplySmall ? styles.small : ""
        }`}
        iconClassName={styles.swiperIcon}
        hideGradient={true}
        onScroll={handleScroll}
      >
        {/* 渲染推荐画板 */}
        {recommendedBoards.boards.map((board, index) => {
          const isCurrentBoard =
            index === 0 && board?.board_id === currentPin?.board?.board_id;
          return (
            <div
              key={`recommended-${stackIndex}-${board?.board_id || index}`}
              className={styles.boardSwiperCard}
              data-board-dist-code={board?.ext?.dist_code}
            >
              <BoardCardNew
                record={board}
                hideButton={true}
                hideUser={true}
                location={index + 1}
                viewSource={isCurrentBoard ? pinId : undefined} // 只有「所在画板」才传递 viewSource
                stackIndex={stackIndex}
                searchWord={searchWord}
                isCurrentBoard={isCurrentBoard}
              />
            </div>
          );
        })}

        {/* 如果有相似画板，渲染分隔卡片 */}
        {similarBoards.boards.length > 0 && (
          <div className={styles.similarBoardsContainer}>
            <div
              key={`similar-boards-separator-${stackIndex}`}
              className={styles.separatorCard}
            >
              <div className={styles.separatorTitle}>
                <span className={styles.separatorLine}></span>
                <span className={styles.separatorText}>相似内容画板</span>
                <span className={styles.separatorLine}></span>
              </div>
            </div>

            {/* 渲染相似画板 */}
            {similarBoards.boards.map((board, index) => {
              return (
                <div
                  key={`similar-${stackIndex}-${board?.board_id || index}`}
                  className={styles.boardSwiperCard}
                  data-module-name="相似画板"
                  data-board-dist-code={board?.ext?.dist_code}
                >
                  <BoardCardNew
                    record={board}
                    hideButton={true}
                    hideUser={true}
                    location={recommendedBoards.boards.length + index + 1}
                    stackIndex={stackIndex}
                    searchWord={searchWord}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* 加载状态提示（基于合并后的状态判断） */}
        {showLoadingFooter && (
          <>
            {hasMore || loading ? (
              <div className={styles.loadingMore}>
                <div className={styles.loadingDot}>加载中...</div>
              </div>
            ) : (
              <div className={styles.noMore}>
                <div className={styles.noMoreText}>没有更多了</div>
              </div>
            )}
          </>
        )}
      </HBSwiper>
    </div>
  );
}

export default memo(BoardSwiper);
